import { useState, useEffect, useRef, useMemo, useCallback } from "react";

// ─── QUESTION BANK ────────────────────────────────────────────────────────────
const QUESTIONS = [
  // ── SET 1 (Q1–Q30) ──
  {
    id: 1,
    text: 'Which document must be filed with the Montreal Exchange (MX) for an account to be eligible to hold "Large Open Positions"?',
    options: ["The Corporate Resolution.", "The Large Open Position Reporting (LOPR) authorization.", "The Hedge Agreement.", "The Standardized Risk Disclosure Statement."],
    correct: 1,
    rationale: "Firms must report accounts exceeding specific thresholds to ensure market oversight and prevent manipulation.",
    topic: "Regulatory Filings & Reporting", difficulty: "medium",
  },
  {
    id: 2,
    text: "A Registered Representative (RR) who wishes to trade derivatives for clients must be registered with which of the following?",
    options: ["The Bank of Canada.", "The Canadian Derivatives Clearing Corporation (CDCC).", "CIRO and the relevant Provincial Securities Commission.", "The Montreal Exchange Board of Directors."],
    correct: 2,
    rationale: "Registration is a provincial requirement, with oversight delegated to CIRO (the SRO).",
    topic: "Registration & Licensing", difficulty: "easy",
  },
  {
    id: 3,
    text: 'A client wants to open a futures account. The exchange requires an "Initial Margin" of $5,000 and a "Maintenance Margin" of $3,800. If the client has a margin deficiency and their equity falls to $3,500, what is the minimum amount they must deposit?',
    options: ["$300", "$1,200", "$1,500", "$5,000"],
    correct: 2,
    rationale: "When an account hits the maintenance level, the client must deposit enough to return the balance to the Initial margin level. $5,000 − $3,500 = $1,500.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 4,
    text: 'A corporation wishes to open an account to hedge its currency exposure. The RR receives the Account Application but notices the "Corporate Resolution" does not explicitly mention "Derivatives." The RR should:',
    options: ["Open the account anyway as the corporation is a legal entity.", "Request a new or amended Corporate Resolution that specifically authorizes derivatives trading.", "Use the RR's own discretion to interpret the resolution.", "Only allow the corporation to trade in cash, not on margin."],
    correct: 1,
    rationale: "Regulatory documentation for corporations must be explicit regarding the types of high-risk activities authorized by the board.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 5,
    text: 'The "Standardized Risk Disclosure Statement" is a mandatory document that explains:',
    options: ["The specific stocks the RR recommends.", "The mechanics of the Montreal Exchange's electronic trading system.", "The risks of leverage, volatility, and potential for total loss of capital.", "The exact commission rates the firm charges."],
    correct: 2,
    rationale: "This is a core disclosure requirement for all retail derivatives accounts to ensure informed consent.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 6,
    text: 'Which of the following is an "Approved Person" under CIRO derivatives rules?',
    options: ["Any client with a net worth over $1 million.", "A Registered Representative, Investment Representative, or Supervisor.", "Any employee of the Canadian Derivatives Clearing Corporation.", "A trader residing in a foreign jurisdiction."],
    correct: 1,
    rationale: "'Approved Person' refers to individuals licensed and authorized by the regulator to conduct or supervise trading activities.",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 7,
    text: 'An RR is preparing a "Hedge Agreement" for a client. To qualify for lower margin rates, the client must certify that:',
    options: ["They have at least 10 years of trading experience.", "The derivative position is intended to offset the risk of a specific physical or cash position.", "They will not trade more than 50 contracts per day.", "They are an institutional investor."],
    correct: 1,
    rationale: "The Hedge Agreement is a legal certification that the trade is defensive, justifying the reduced risk (and margin) profile.",
    topic: "Margin Documentation", difficulty: "hard",
  },
  {
    id: 8,
    text: 'How long must a firm retain a copy of a client\'s signed "Derivatives Trading Agreement" after the account is closed?',
    options: ["1 year.", "3 years.", "7 years.", "10 years."],
    correct: 2,
    rationale: "General CIRO and provincial record-keeping rules require 7 years of retention for client documentation.",
    topic: "Compliance & Supervision", difficulty: "easy",
  },
  {
    id: 9,
    text: 'A client signs the options agreement but refuses to sign the "Hypothecation Agreement." The firm:',
    options: ["Can only allow the client to buy options (no writing).", "Must refuse to open a margin-based derivatives account.", "Can use the client's securities as collateral anyway.", "Must report the client to FINTRAC."],
    correct: 1,
    rationale: "Margin accounts require a Hypothecation Agreement to allow the dealer to use the securities as collateral for the margin loan.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 10,
    text: '"UMIR" stands for:',
    options: ["Universal Market Integrity Rules.", "Unified Margin and Insurance Requirements.", "Underlying Market Investment Regulations.", "Universal Montreal Index Rules."],
    correct: 0,
    rationale: "UMIR are the rules governing trading practices and market integrity for all Canadian equity marketplaces.",
    topic: "Market Integrity & UMIR", difficulty: "easy",
  },
  {
    id: 11,
    text: 'Which document is used to track "Ultimate Beneficial Ownership" for anti-money laundering (AML) purposes at account opening?',
    options: ["The Margin Agreement.", "The AML/KYC Supplement (or Beneficial Ownership Form).", "The Risk Disclosure Statement.", "The Monthly Account Statement."],
    correct: 1,
    rationale: "Federal AML laws require firms to identify the individuals who actually control the funds in the account.",
    topic: "AML & FINTRAC", difficulty: "medium",
  },
  {
    id: 12,
    text: "When a Registered Representative moves from one CIRO-member firm to another, they must:",
    options: ["Retake the Derivatives Fundamentals Course.", "Transfer their registration through the National Registration Database (NRD).", "Obtain a new passport.", "Wait 12 months before trading again."],
    correct: 1,
    rationale: "The NRD is the centralized system for tracking the registration status of all 'Approved Persons.'",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 13,
    text: "A client's account has a Margin Deficiency of $2,500. If the firm liquidates and the loss is $3,200, who is responsible for the remaining $700?",
    options: ["The firm.", "The CDCC.", "The client.", "The Montreal Exchange."],
    correct: 2,
    rationale: "Client agreements state that the investor is legally responsible for all losses, including those exceeding the margin deposited.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 14,
    text: 'A "Managed Account Agreement" is required when:',
    options: ["The RR provides any advice to the client.", "The client has more than $1,000,000 in assets.", "The RR is given discretionary authority to make trades without the client's prior consent.", "The client is a non-resident of Canada."],
    correct: 2,
    rationale: "Discretionary authority is a high-risk delegation that requires a specific legal contract.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 15,
    text: 'The "Information Summary" provided by the CDCC must be given to:',
    options: ["Only institutional clients.", "All clients prior to their first trade in cleared derivatives.", "Only clients who are in a margin call.", "Only RRs, not clients."],
    correct: 1,
    rationale: "This summary provides standardized info on the clearing process and the roles of the CDCC.",
    topic: "CDCC & Clearing", difficulty: "easy",
  },
  {
    id: 16,
    text: 'Which of the following is a "Prohibited Practice" according to UMIR?',
    options: ["Buying a protective put.", '"Front-running" a client order.', "Hedging a currency position.", "Trading in a managed account."],
    correct: 1,
    rationale: "Front-running is entering an order for the RR's benefit before a client order that the RR knows is coming.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 17,
    text: 'For "Exempt Board Lots" on the Montreal Exchange, which regulatory filing is usually waived?',
    options: ["The KYC Form.", "The Daily Mark-to-Market.", "The LOPR reporting for that specific small position.", "The Risk Disclosure Statement."],
    correct: 2,
    rationale: "Reporting thresholds (LOPR) exist so that regulators aren't overwhelmed by small, non-manipulative positions.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 18,
    text: 'A Registered Representative must disclose "Conflicts of Interest" in writing to the client:',
    options: ["Only if the RR is losing money.", "Before or at the time of the recommendation or trade.", "Once a year in the annual report.", "Only if the client is an institutional investor."],
    correct: 1,
    rationale: "Immediate transparency is a cornerstone of the Client Focused Reforms.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 19,
    text: 'Which regulatory body oversees the "Provincial" licensing of RRs in Ontario?',
    options: ["The Montreal Exchange.", "The Ontario Securities Commission (OSC).", "The Bank of Canada.", "The Toronto Stock Exchange (TSX)."],
    correct: 1,
    rationale: "While CIRO is the SRO, the OSC is the ultimate provincial authority in Ontario.",
    topic: "Registration & Licensing", difficulty: "easy",
  },
  {
    id: 20,
    text: 'A client opens an account for their spouse through a "Power of Attorney" (POA). The RR must verify:',
    options: ["That the POA is legally valid and specifically authorizes derivatives trading.", "That the spouse has at least $100,000 in income.", "That the RR is named as the beneficiary.", "That the client lives in the same house."],
    correct: 0,
    rationale: "POA authority is strictly interpreted; it must be valid and cover the specific high-risk activity being performed.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 21,
    text: 'A "Trade Confirmation" must be sent to the client:',
    options: ["Once a month.", "Only if the client requests it.", "Promptly after the execution of each trade.", "Only if the trade was profitable."],
    correct: 2,
    rationale: "Confirmations are the official record of price, quantity, and commission for every transaction.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 22,
    text: '"Position Limits" are defined as the maximum number of contracts that:',
    options: ["A firm can trade in one day.", "An RR can manage in total.", "A single investor or group of investors can hold on the same side of the market.", "The CDCC can clear in one session."],
    correct: 2,
    rationale: "Limits prevent any single party from cornering the market or creating artificial volatility.",
    topic: "Regulatory Filings & Reporting", difficulty: "medium",
  },
  {
    id: 23,
    text: 'If an investor is a "Non-Resident" of Canada, the firm must ensure:',
    options: ["They never trade derivatives.", "They are only allowed to trade with $10,000 or less.", "The account opening complies with both Canadian laws and the laws of the client's home jurisdiction.", "They sign a 'Waiver of Canadian Law.'"],
    correct: 2,
    rationale: "Cross-border compliance is essential to avoid regulatory violations in either country.",
    topic: "Account Documentation", difficulty: "hard",
  },
  {
    id: 24,
    text: 'Which document details the "Rights and Obligations" of the buyer and seller of an exchange-traded option?',
    options: ["The Relationship Disclosure Information (RDI).", "The CDCC Rules and Manual.", "The RR's personal business card.", "The Montreal Exchange Trading Floor Guide."],
    correct: 1,
    rationale: "The CDCC manual is the legal governing document for the standardized terms of listed contracts.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 25,
    text: 'A client holds 500 calls on ABC. The exchange announces a "Position Limit" of 2,000 contracts. The client\'s spouse also holds 1,600 calls in a separate account. If they are considered a "Group," are they in violation?',
    options: ["No, because each account is under 2,000.", "Yes, because the combined total of 2,100 exceeds the limit.", "No, because spouses are not considered a group.", "Yes, but only if they trade on the same day."],
    correct: 1,
    rationale: "Regulators aggregate 'related' accounts (acting in concert) when calculating position limits. 500 + 1,600 = 2,100.",
    topic: "Regulatory Filings & Reporting", difficulty: "medium",
  },
  {
    id: 26,
    text: 'A "Designated Supervisor" is responsible for:',
    options: ["Executing trades for the house account.", "Reviewing and approving new derivatives accounts and supervising RR conduct.", "Setting the prime interest rate.", "Managing the exchange's IT systems."],
    correct: 1,
    rationale: "Supervision is the second line of defense in protecting the client and the firm.",
    topic: "Compliance & Supervision", difficulty: "easy",
  },
  {
    id: 27,
    text: 'The "Monthly Account Statement" must include:',
    options: ["Only the trades made in the last 30 days.", "A list of all open positions and their current market value (Mark-to-Market).", "The RR's personal cell phone number.", "The firm's quarterly profit report."],
    correct: 1,
    rationale: "Statements provide the 'snapshot' of the client's current financial standing and risk.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 28,
    text: '"Self-Regulation" in the Canadian derivatives market means:',
    options: ["Investors decide their own margin rates.", "The industry (through CIRO) creates and enforces its own rules under government oversight.", "The Montreal Exchange operates without any laws.", "RRs do not need to be licensed."],
    correct: 1,
    rationale: "SROs allow the industry to use its expertise to regulate while remaining accountable to public commissions (like the OSC).",
    topic: "Registration & Licensing", difficulty: "easy",
  },
  {
    id: 29,
    text: 'A client wants to open a "Joint Account" with a business partner. The RR must ensure:',
    options: ["Both partners sign the account documents and provide KYC info.", "Only the partner with the most money signs.", "They use the business's tax ID only.", "The partners are married."],
    correct: 0,
    rationale: "Joint owners are 'jointly and severally' liable, so both must be fully vetted.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 30,
    text: "Which of the following is NOT a mandatory filing for a CIRO-member firm?",
    options: ["Quarterly Financial Reports.", "Large Open Position Reports (LOPR).", "Daily Trading Volume by Client Name for the public.", "Margin Deficiency Reports."],
    correct: 2,
    rationale: "While firms report data to regulators, client-specific trading details are private and not for public filing.",
    topic: "Regulatory Filings & Reporting", difficulty: "medium",
  },
  // ── SET 2 (Q31–Q60) ──
  {
    id: 31,
    text: 'Under CIRO Rule 3200, a "Material Change" in a client\'s KYC information must be documented and approved by a Designated Supervisor within:',
    options: ["24 hours.", "10 business days.", "30 days.", "The end of the calendar month."],
    correct: 2,
    rationale: "Firms have a 30-day window to update and approve significant changes to a client's financial or risk profile.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 32,
    text: 'The "Risk Disclosure Statement" for futures must explicitly warn the client that:',
    options: ["They can lose more than the money they deposited in the account.", "Futures prices always move in the same direction as the spot price.", "The clearinghouse guarantees a profit for all hedgers.", "Commissions are tax-deductible for all investors."],
    correct: 0,
    rationale: "Unlike buying options, where risk is limited to the premium, futures involve leverage that can lead to losses exceeding the initial margin.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 33,
    text: "A client holds 400 call options on a stock. The exchange declares a Position Limit of 2,500 contracts for that stock. The client is also a 100% owner of a corporation that holds 2,200 call options. Is the client in violation?",
    options: ["No, because the individual account is well below the limit.", "Yes, because the combined 'Control' position of 2,600 exceeds the limit.", "No, because corporations and individuals are treated as separate legal entities for limits.", "Yes, but only if they exercise more than 100 contracts at once."],
    correct: 1,
    rationale: "Position limits apply to all accounts over which a person has direct or indirect control. 400 + 2,200 = 2,600.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 34,
    text: 'A client asks if the CDCC "Information Summary" is a contract. The RR should respond that:',
    options: ["Yes, it is the primary trading agreement.", "No, it is a disclosure document summarizing the clearinghouse's roles and the risks of the products.", "Yes, it replaces the firm's Margin Agreement.", "No, it is only required for US-based clients."],
    correct: 1,
    rationale: "The Information Summary is a regulatory disclosure, not the legal trading agreement between the client and the dealer.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 35,
    text: "Which specific registration is required for an individual to supervise the derivatives trading of other Registered Representatives?",
    options: ["Chief Compliance Officer (CCO).", "Derivatives Fundamentals Course (DFC) certificate only.", "Derivatives Supervisor (or Designated Supervisor).", "Portfolio Manager (PM)."],
    correct: 2,
    rationale: "Supervisors must pass specific exams (like the DSS) and be registered as a supervisor with CIRO to oversee high-risk derivatives activity.",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 36,
    text: 'A "Restricted" registration for an RR means they can only:',
    options: ["Trade for institutional clients.", "Trade certain types of derivatives (e.g., options only, not futures).", "Trade during daytime hours.", "Trade for their own family members."],
    correct: 1,
    rationale: "Some RRs are only licensed for specific products depending on the courses they have completed and their registration status.",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 37,
    text: 'According to CDCC rules, if a clearing member firm becomes insolvent, the "Default Waterfall" uses which of the following FIRST to cover losses?',
    options: ["The CDCC's own corporate capital.", "The contributions of all other non-defaulting clearing members.", "The margin and clearing fund contributions of the defaulting member.", "The Bank of Canada's emergency fund."],
    correct: 2,
    rationale: "To protect the system, the defaulter's own collateral is the first line of defense before mutualized funds are touched.",
    topic: "CDCC & Clearing", difficulty: "hard",
  },
  {
    id: 38,
    text: 'The "Hypothecation Agreement" specifically allows a broker to:',
    options: ["Charge a client for a research report.", "Pledge a client's securities as collateral for a loan from a bank to fund the client's margin.", "Execute a trade without the client's permission.", "Share the client's phone number with other brokers."],
    correct: 1,
    rationale: "This is the legal mechanism that makes margin lending possible.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 39,
    text: "A client's Large Open Position in a specific option series is 250 contracts. The LOPR reporting threshold is 200 contracts. The firm fails to report this for 5 days. Who is primarily responsible for this reporting failure?",
    options: ["The individual client.", "The Montreal Exchange IT department.", "The clearing member firm (the dealer).", "The CDCC."],
    correct: 2,
    rationale: "Reporting of large positions is a firm-level regulatory obligation.",
    topic: "Regulatory Filings & Reporting", difficulty: "medium",
  },
  {
    id: 40,
    text: 'A "Conflicts of Interest" disclosure must be:',
    options: ["Printed on the back of every business card.", "Provided in writing in a clear and prominent manner.", "Discussed only if the trade is over $1,000,000.", "Filed with the RCMP."],
    correct: 1,
    rationale: "Transparency must be proactive and easily understood by the client.",
    topic: "Compliance & Supervision", difficulty: "easy",
  },
  {
    id: 41,
    text: 'Which of the following is an "Approved Marketplace" for derivatives in Canada?',
    options: ["The Toronto Stock Exchange (TSX).", "The Montreal Exchange (MX).", "The Canadian Securities Exchange (CSE).", "The Bank of Canada."],
    correct: 1,
    rationale: "The Montreal Exchange is the sole dedicated exchange for listed financial derivatives in Canada.",
    topic: "Registration & Licensing", difficulty: "easy",
  },
  {
    id: 42,
    text: 'An RR is working with a "Commercial Hedger" who refuses to sign the standard Risk Disclosure Statement. The RR should:',
    options: ["Waive the requirement because the client is a professional.", "Refuse to open the account, as this is a mandatory regulatory requirement for all accounts.", "Only allow the client to trade in cash.", "Ask the client to write their own disclosure."],
    correct: 1,
    rationale: "Regulators do not allow 'opt-outs' for mandatory disclosures, even for sophisticated or commercial entities.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 43,
    text: 'The "Order Protection Rule" (OPR) is designed to ensure that:',
    options: ["Clients never lose money on a trade.", "No trade is executed at a price inferior to a visible better-priced order on another marketplace.", "RRs are protected from being sued by clients.", "Margin calls are only made during business hours."],
    correct: 1,
    rationale: "OPR promotes market efficiency and fairness by ensuring the best prices are always respected.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 44,
    text: '"Standardized Terms" for an option contract include all of the following EXCEPT:',
    options: ["The expiration date.", "The strike price.", "The multiplier.", "The commission rate charged by the broker."],
    correct: 3,
    rationale: "Commissions are negotiated between the broker and client; they are not part of the standardized contract terms set by the exchange/clearinghouse.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 45,
    text: 'An investor wants to buy 10 futures contracts. The "Initial Margin" is $3,000 per contract. The client has $25,000 in their account. Can they open the position?',
    options: ["Yes, because they have more than $3,000.", "No, because they need $30,000 to open 10 contracts.", "Yes, because they only need Maintenance Margin ($2,250) to open.", "No, because they must have $100,000 for any futures trade."],
    correct: 1,
    rationale: "Opening a position requires the full Initial Margin for the entire block. 10 × $3,000 = $30,000.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 46,
    text: 'Which document must be signed to permit "Cross-Guaranteed" accounts where equity from one account can cover a deficiency in another?',
    options: ["The Joint Account Agreement.", "The Guarantee and Postponement of Claim Agreement.", "The Hypothecation Agreement.", "The Corporate Resolution."],
    correct: 1,
    rationale: "This legal document allows the firm to treat multiple accounts as one for margin purposes.",
    topic: "Margin Documentation", difficulty: "hard",
  },
  {
    id: 47,
    text: 'A Registered Representative must notify CIRO of a change in their personal "Material Information" (e.g., a criminal charge) within:',
    options: ["24 hours.", "2 business days.", "10 business days.", "30 days."],
    correct: 2,
    rationale: "Personal material changes for 'Approved Persons' must be reported via the NRD within 10 days.",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 48,
    text: '"Best Execution" policies of a firm must be reviewed:',
    options: ["Every 5 years.", "Once a month.", "At least annually.", "Only if a client complains."],
    correct: 2,
    rationale: "Firms must ensure their execution methods remain competitive and effective for clients.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 49,
    text: 'An "Exercise Notice" submitted to the CDCC is "Irrevocable." This means:',
    options: ["The client can change their mind if the stock price moves.", "Once the notice is submitted, the exercise process must be completed.", "The clearinghouse can refuse to process it.", "The RR can cancel it if the client made a mistake."],
    correct: 1,
    rationale: "Irrevocability ensures certainty in the settlement process for both parties.",
    topic: "CDCC & Clearing", difficulty: "hard",
  },
  {
    id: 50,
    text: 'The "Universal Market Integrity Rules" (UMIR) apply to:',
    options: ["Only the Montreal Exchange.", "All marketplaces in Canada where equity or derivative securities are traded.", "Only US-based traders in Canada.", "Only institutional clients."],
    correct: 1,
    rationale: "UMIR provides a consistent regulatory floor for all Canadian trading venues.",
    topic: "Market Integrity & UMIR", difficulty: "easy",
  },
  {
    id: 51,
    text: "An investor's position is worth $100,000 in notional value. The firm's House Margin requirement is 15%, while the CDCC Regulatory Margin is 10%. Which amount must the client maintain?",
    options: ["$10,000", "$15,000", "$25,000", "$5,000"],
    correct: 1,
    rationale: "Firms are allowed to charge more than the regulatory minimum, but never less. The client must meet the more stringent requirement.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 52,
    text: '"Front-running" is considered a violation of which pillar of derivatives regulation?',
    options: ["Market Integrity.", "Client Suitability.", "Margin Capitalization.", "Privacy and Confidentiality."],
    correct: 0,
    rationale: "It is an unfair practice that exploits non-public order information.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 53,
    text: "A client wants to trade derivatives in an RRSP account. The RR must verify that:",
    options: ["The options are 'Qualified Investments' under the Income Tax Act.", "The client is over the age of 65.", "The client only buys puts, never calls.", "The RR receives a higher commission for registered accounts."],
    correct: 0,
    rationale: "Not all derivatives are permitted in registered accounts; tax compliance is as important as regulatory compliance here.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 54,
    text: 'Which document is required to verify that a client has the "Legal Capacity" to enter into a derivatives contract?',
    options: ["The Risk Disclosure Statement.", "The Account Application / KYC form (with ID verification).", "The Monthly Statement.", "The Trade Confirmation."],
    correct: 1,
    rationale: "Confirming the identity and legal status (e.g., age of majority) is the first step in account opening.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 55,
    text: '"Naked" option writing refers to:',
    options: ["Trading without a shirt on.", "Selling an option without holding the underlying asset or an offsetting position.", "Trading on the OTC market.", "Buying an option with no intention of exercising it."],
    correct: 1,
    rationale: "Naked writing carries significant risk and requires the most stringent margin documentation.",
    topic: "Margin Documentation", difficulty: "easy",
  },
  {
    id: 56,
    text: 'A "Designated Supervisor" must approve all "Advertising" and "Sales Literature" related to derivatives:',
    options: ["After it has been sent to clients.", "Only if it is televised.", "Prior to its use or distribution.", "Once every two years."],
    correct: 2,
    rationale: "Pre-approval prevents misleading claims from reaching the public.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 57,
    text: "A client's margin account has $8,000 in equity. They have a margin requirement of $10,000. If the client ignores the Margin Call for 48 hours, the firm has the legal right to:",
    options: ["Sue the client's family.", "Liquidate enough positions to cover the deficiency immediately.", "Wait until the end of the month.", "Do nothing."],
    correct: 1,
    rationale: "The Derivatives Trading Agreement grants the firm the right to protect its capital by liquidating positions in a margin deficiency.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 58,
    text: 'Which entity is responsible for "Marking-to-Market" all open futures positions every day?',
    options: ["The individual Registered Representative.", "The Canadian Derivatives Clearing Corporation (CDCC).", "The client's accountant.", "The provincial securities commission."],
    correct: 1,
    rationale: "The clearinghouse settles all gains and losses daily to ensure system liquidity.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 59,
    text: 'A "Fiduciary" duty is different from a "Best Interest" duty because it:',
    options: ["Is easier to comply with.", "Is the highest legal standard of care and loyalty, often required in discretionary accounts.", "Does not apply to institutional clients.", "Only applies to cash accounts."],
    correct: 1,
    rationale: "While all RRs must act in the 'best interest,' managed account providers often carry a higher fiduciary burden.",
    topic: "Compliance & Supervision", difficulty: "hard",
  },
  {
    id: 60,
    text: "Which regulatory filing is required when an investor's aggregate position in an option class exceeds MX thresholds?",
    options: ["The KYC Update.", "The Large Open Position Report (LOPR).", "The Corporate Resolution.", "The Standardized Risk Disclosure."],
    correct: 1,
    rationale: "LOPR is the primary tool for monitoring market concentration.",
    topic: "Regulatory Filings & Reporting", difficulty: "easy",
  },
  // ── SET 3 (Q61–Q90) ──
  {
    id: 61,
    text: 'A "Qualified Hedger" is defined by the clearinghouse as an account that is exempt from which of the following?',
    options: ["The requirement to sign a Risk Disclosure Statement.", "Paying any commission to the dealer.", "The higher 'Speculative' margin rates.", "The duty of Best Execution."],
    correct: 2,
    rationale: "By signing a Hedge Agreement and proving the existence of an underlying physical position, the client is granted lower margin rates because their net risk is reduced.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 62,
    text: 'Which of the following must be included in the "Relationship Disclosure Information" (RDI) regarding the "Trusted Contact Person" (TCP)?',
    options: ["The TCP's home address and phone number.", "A statement that the firm may contact the TCP to protect the client's financial interests in specific circumstances.", "The TCP's personal credit score.", "A guarantee that the TCP will be notified of every trade."],
    correct: 1,
    rationale: "The RDI must explain the purpose of the TCP so the client understands how this protective measure works.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 63,
    text: "A client wants to trade on the Montreal Exchange from their office in France. If the Canadian withholding tax on certain distributions is 15% and the client's home country has no tax treaty, what is the default Part XIII tax rate usually applied?",
    options: ["5%", "15%", "25%", "30%"],
    correct: 3,
    rationale: "The standard statutory withholding for non-residents in Canada without a treaty is 30%. Regulatory documentation must account for these financial impacts.",
    topic: "Account Documentation", difficulty: "hard",
  },
  {
    id: 64,
    text: "An RR receives an order from a client to sell 10,000 index futures. The RR immediately sells 50 contracts for their own personal account before entering the client's order. This is a violation of:",
    options: ["The Suitability Rule.", "The Order Protection Rule.", "The Front-Running Rule (UMIR 4.1).", "The Margin Deficiency Rule."],
    correct: 2,
    rationale: "Front-running is the illegal practice of trading ahead of a client's non-public order to gain an unfair advantage.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 65,
    text: 'A "Designated Supervisor" is required to review and initial all "New Account Applications" for derivatives:',
    options: ["Within 30 days of the first trade.", "Prior to the first trade being executed.", "Only if the client is a retail investor.", "At the end of each fiscal quarter."],
    correct: 1,
    rationale: "The supervisor acts as a gatekeeper to ensure that the account is suitable and the documentation is complete before risk is taken.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 66,
    text: 'The "Canadian Derivatives Clearing Corporation" (CDCC) is regulated by:',
    options: ["The Bank of Canada and various provincial securities commissions.", "The Montreal Exchange exclusively.", "The US Securities and Exchange Commission (SEC).", "The Investment Industry Association of Canada (IIAC)."],
    correct: 0,
    rationale: "As a 'Systemically Important' clearinghouse, the CDCC is overseen by the central bank and provincial regulators (like the OSC and AMF).",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 67,
    text: "If an investor's account is Marked-to-Market and shows a gain of $1,000, when is this cash typically available for the client to withdraw?",
    options: ["Only after the position is closed.", "On a T+1 basis (the next business day).", "Immediately at 4:00 PM on the day of the trade.", "At the end of the calendar month."],
    correct: 1,
    rationale: "Daily settlement moves cash between accounts overnight; the realized gain is available the next business day.",
    topic: "CDCC & Clearing", difficulty: "hard",
  },
  {
    id: 68,
    text: 'The "Default Waterfall" is a regulatory framework designed to:',
    options: ["Ensure RRs are paid their commissions first.", "Protect the clearinghouse and the market from the failure of a single member firm.", "Increase the volatility of the Montreal Exchange.", "Prevent retail clients from using margin."],
    correct: 1,
    rationale: "The waterfall dictates the sequence of assets used to cover a default, ensuring the system remains solvent.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 69,
    text: 'A client wishes to open an account for "Arbitrage" between the Montreal Exchange and the Chicago Board of Trade. The RR must provide:',
    options: ["Only Canadian disclosure documents.", "Both Canadian and US-equivalent risk disclosure documents.", "No documents, as arbitrage is a professional strategy.", "A guarantee that the prices will converge."],
    correct: 1,
    rationale: "Compliance requires ensuring the client is informed of the risks in every jurisdiction where they are trading.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 70,
    text: 'An RR is disciplined for a "Minor Rule Violation." What is the maximum fine for an individual under the CIRO "Minor Rule Violation" (MRV) program?',
    options: ["$1,000", "$5,000", "$25,000", "$100,000"],
    correct: 1,
    rationale: "While fines for major violations are higher, the MRV program for individuals is capped at $5,000 per violation.",
    topic: "Compliance & Supervision", difficulty: "hard",
  },
  {
    id: 71,
    text: 'Which document is used to authorize the "Transfer" of an existing derivatives position from one dealer to another?',
    options: ["The Risk Disclosure Statement.", "The Account Transfer Authorization Form (ATA).", "The Hypothecation Agreement.", "The CDCC Information Summary."],
    correct: 1,
    rationale: "This document provides the legal instructions to move assets and historical cost basis between firms.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 72,
    text: '"Material Non-Public Information" (MNPI) becomes "Public" only when:',
    options: ["An RR tells their best client.", "It has been disseminated through a broad, public news service and the market has had time to absorb it.", "It is posted on an anonymous social media account.", "The RR's manager signs off on it."],
    correct: 1,
    rationale: "Insider trading laws depend on the strict definition of what constitutes a public disclosure.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 73,
    text: 'A "Restricted Portfolio Manager" is a registration category that allows an individual to:',
    options: ["Trade all derivatives for all clients.", "Manage discretionary accounts but only for specific types of assets (e.g., only options).", "Only work for the Montreal Exchange.", "Trade without a KYC form."],
    correct: 1,
    rationale: "Restrictions are based on the individual's specific qualifications and the firm's license.",
    topic: "Registration & Licensing", difficulty: "medium",
  },
  {
    id: 74,
    text: 'The "Client Relationship Model" (CRM) requires that "Commissions" for a trade be disclosed:',
    options: ["Only if the client asks.", "In the Trade Confirmation provided after the execution.", "At the end of the year in a summary report.", "Only if the commission is over $100."],
    correct: 1,
    rationale: "Full transparency of costs for every transaction is mandatory.",
    topic: "Account Documentation", difficulty: "easy",
  },
  {
    id: 75,
    text: 'An "Option Class" refers to all options that:',
    options: ["Have the same strike price.", "Have the same expiration date.", "Are of the same type (Call or Put) on the same underlying security.", "Are traded by the same firm."],
    correct: 2,
    rationale: "This is a technical definition used for calculating position limits and regulatory reporting.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 76,
    text: '"Reporting Thresholds" for LOPR are based on the total number of contracts on:',
    options: ["One side of the market (e.g., all long calls and short puts).", "Both sides combined (long and short).", "Only the long side.", "Only the short side."],
    correct: 0,
    rationale: "Regulators aggregate positions that have the same directional bias (e.g., those that benefit if the stock rises).",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 77,
    text: 'A client wants to trade derivatives in a "Partnership" account. The RR must obtain the signature of:',
    options: ["Only the managing partner.", "All partners who have the authority to bind the partnership as per the partnership agreement.", "Every single partner, regardless of their role.", "The partnership's bank manager."],
    correct: 1,
    rationale: "Documentation must align with the partnership's legal structure and the 'Corporate Resolution' equivalent.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 78,
    text: 'Which of the following is an "Illegal Practice" according to UMIR 2.2 regarding "Wash Trades"?',
    options: ["Buying a stock in one account and selling it in a different account with the same beneficial owner.", "Buying a call and a put simultaneously.", "Hedging a short stock position with a call.", "Rolling an option to a later month."],
    correct: 0,
    rationale: "Wash trades create a false impression of volume without any real change in ownership.",
    topic: "Market Integrity & UMIR", difficulty: "hard",
  },
  {
    id: 79,
    text: 'The "Exercise" of an equity option in Canada results in the delivery of shares on which timeline?',
    options: ["T (Same day).", "T+1 (Next day).", "T+2 (Two business days).", "T+5 (One week)."],
    correct: 1,
    rationale: "The Canadian market moved to T+1 for many settlement processes, and exercise of derivatives follows the accelerated T+1 cycle.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 80,
    text: "A firm has a Regulatory Capital requirement of $250,000. If the firm's current Risk Adjusted Capital (RAC) falls to $200,000, the firm must:",
    options: ["Hire more RRs.", "Notify CIRO immediately and potentially stop trading.", "Borrow money from its clients.", "Increase its commission rates."],
    correct: 1,
    rationale: "Capital requirements are 'red lines.' Falling below them is a serious regulatory event.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 81,
    text: '"Conflict of Interest" rules require firms to "Avoid" conflicts that:',
    options: ["Are too expensive to manage.", "Cannot be addressed in the client's best interest.", "Involve less than $1,000.", "Only involve institutional clients."],
    correct: 1,
    rationale: "If disclosure and controls aren't enough to protect the client, the firm must walk away from the conflict.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 82,
    text: 'An "Information Summary" for a new derivative product must be approved by:',
    options: ["The RR's manager.", "The Montreal Exchange and CIRO.", "The Federal Ministry of Finance.", "The individual client."],
    correct: 1,
    rationale: "Standardization ensures that all firms provide the same quality of disclosure for complex products.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 83,
    text: 'A client complains they weren\'t informed about the risks of "Early Assignment" on a short call. The RR points to the signed "Risk Disclosure Statement." Is the firm protected?',
    options: ["Yes, if the document specifically mentioned early assignment and the client signed it.", "No, the RR must verbally explain every risk every day.", "No, the Risk Disclosure Statement is not a legal document.", "Yes, but only if the RR also sent the client an email that morning."],
    correct: 0,
    rationale: "Standardized disclosures are designed to protect both the client (through information) and the firm (through evidence of informed consent).",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 84,
    text: '"Large Open Position Reporting" (LOPR) is submitted to the exchange:',
    options: ["Monthly.", "Weekly.", "Daily.", "Only when a position is closed."],
    correct: 2,
    rationale: "Daily reporting allows regulators to monitor market swings and concentration in real-time.",
    topic: "Regulatory Filings & Reporting", difficulty: "easy",
  },
  {
    id: 85,
    text: 'Which Greek is the primary focus of "Delta-Neutral" regulatory reporting for market makers?',
    options: ["Gamma.", "Delta.", "Vega.", "Theta."],
    correct: 1,
    rationale: "Delta measures the net directional exposure, which is the key metric for neutrality.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 86,
    text: "An investor holds 1,500 net long calls. The reporting threshold is 200. The position is part of a group of 5 related accounts. How many LOPR records must the firm submit for this group?",
    options: ["1", "5", "1,500", "0"],
    correct: 1,
    rationale: "While the position is aggregated for limits, each account in the group must be reported individually.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 87,
    text: 'A "Stop-Limit" order is triggered by a trade, but it may never be executed if:',
    options: ["The stock price moves past the limit price too quickly.", "The RR goes on lunch.", "The Montreal Exchange closes for a holiday.", "The clearinghouse refuses the trade."],
    correct: 0,
    rationale: "Unlike a Market order, a Limit order will only fill at the specified price or better.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 88,
    text: 'The "Investment Industry Association of Canada" (IIAC) is primarily a:',
    options: ["Regulatory body with the power to fine RRs.", "Government department.", "Member-based professional association representing the interests of investment firms.", "Clearinghouse."],
    correct: 2,
    rationale: "Unlike CIRO (the regulator), IIAC is an advocacy group for the industry.",
    topic: "Registration & Licensing", difficulty: "easy",
  },
  {
    id: 89,
    text: '"Acting in Concert" is a regulatory term used to describe:',
    options: ["Two RRs working on the same team.", "Multiple accounts controlled by different people who are following a single trading plan to circumvent limits.", "A client who trades in two different currencies.", "A firm that uses two different clearinghouses."],
    correct: 1,
    rationale: "Regulators look through the 'account names' to find the real decision-making influence.",
    topic: "Regulatory Filings & Reporting", difficulty: "hard",
  },
  {
    id: 90,
    text: 'A Registered Representative must disclose if a recommended security is a "Proprietary Product" because:',
    options: ["It is usually more expensive.", "It creates an inherent conflict of interest for the firm.", "Proprietary products are not covered by CIPF.", "The Montreal Exchange does not allow proprietary trading."],
    correct: 1,
    rationale: "Best Interest rules require extra scrutiny when the firm stands to gain more from a specific 'house' product.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  // ── SET 4 (Q91–Q100) ──
  {
    id: 91,
    text: 'Under UMIR, "Inter-listed" securities (listed on both the MX and a US exchange) require the RR to:',
    options: ["Only follow US regulations.", "Ensure that the client has a US bank account.", "Comply with the best execution and order protection rules of the Canadian marketplace first for Canadian orders.", "Ignore Canadian margin rules."],
    correct: 2,
    rationale: "Even if a security is inter-listed, Canadian RRs are bound by CIRO and UMIR standards for their Canadian clients.",
    topic: "Market Integrity & UMIR", difficulty: "medium",
  },
  {
    id: 92,
    text: 'The "Default Fund" of the CDCC is funded by:',
    options: ["The Federal Government.", "Fines collected from retail investors.", "Contributions from all clearing member firms.", "The interest earned on client cash balances."],
    correct: 2,
    rationale: "Clearing members 'mutualize' risk by contributing to a fund that covers losses if one of the members fails.",
    topic: "CDCC & Clearing", difficulty: "medium",
  },
  {
    id: 93,
    text: 'A firm discovers an error in a "Trade Confirmation" where a client was charged a $500 commission instead of the agreed $50. According to regulatory standards, the firm must:',
    options: ["Do nothing if the trade was profitable.", "Issue a 'Corrected Confirmation' and refund the $450 immediately.", "Wait for the client to notice and complain.", "Only fix it if the client makes another trade."],
    correct: 1,
    rationale: "Firms have a duty to ensure the accuracy of trade records and must proactively correct financial errors.",
    topic: "Account Documentation", difficulty: "medium",
  },
  {
    id: 94,
    text: '"FINTRAC" is the Canadian agency responsible for collecting documentation related to:',
    options: ["The strike prices of options.", "The educational background of RRs.", "Suspicious transactions and money laundering.", "The daily trading volume of the Montreal Exchange."],
    correct: 2,
    rationale: "FINTRAC oversees the compliance of financial entities with the Proceeds of Crime (Money Laundering) and Terrorist Financing Act.",
    topic: "AML & FINTRAC", difficulty: "easy",
  },
  {
    id: 95,
    text: 'A client wants to open an account for their "Investment Club." The RR must ensure the documentation includes:',
    options: ["The names and KYC information for all members of the club with a significant interest.", "Only the name of the club president.", "A copy of the club's bylaws regarding social events.", "A guarantee from each member's employer."],
    correct: 0,
    rationale: "Anti-money laundering and suitability rules require identifying the individuals behind the 'entity' (the club).",
    topic: "AML & FINTRAC", difficulty: "medium",
  },
  {
    id: 96,
    text: "A Designated Supervisor who fails to properly review a series of high-risk trades in a managed account may be:",
    options: ["Promoted for increasing volume.", "Subject to disciplinary action by CIRO, including fines or suspension.", "Exempt from liability if the trades were profitable.", "Required to retake their driver's license exam."],
    correct: 1,
    rationale: "Supervisors carry legal responsibility for the conduct of the RRs they oversee.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 97,
    text: '"Trade Finality" in the Canadian derivatives market is achieved when:',
    options: ["The RR tells the client the trade is done.", "The trade is matched and accepted by the CDCC for clearing.", "The client receives their monthly statement.", "The option expires."],
    correct: 1,
    rationale: "Acceptance by the clearinghouse (CDCC) triggers novation, making the trade a legally binding obligation of the clearinghouse.",
    topic: "CDCC & Clearing", difficulty: "hard",
  },
  {
    id: 98,
    text: "If a firm's Policy Manual is more restrictive than CIRO's minimum requirements, the RR must follow:",
    options: ["CIRO's rules only.", "The firm's manual.", "Whichever one is easier at the moment.", "The client's instructions."],
    correct: 1,
    rationale: "Regulatory standards are the floor. Firms often set higher ceilings (internal policies) that their employees are contractually obligated to follow.",
    topic: "Compliance & Supervision", difficulty: "medium",
  },
  {
    id: 99,
    text: 'A "Letter of Guarantee" is a document used to:',
    options: ["Promise a client a 10% return.", "Allow an approved financial institution to guarantee the margin of a specific client's position.", "Authorize an RR to trade on their own behalf.", "Cancel a trade after it has been executed."],
    correct: 1,
    rationale: "This is a specialized margin document often used by institutional clients to satisfy requirements through a bank's credit.",
    topic: "Margin Documentation", difficulty: "medium",
  },
  {
    id: 100,
    text: 'The "Risk Disclosure Statement" must be delivered to a client ______ their first trade.',
    options: ["At least 24 hours after.", "Within 30 days of.", "Before or at the time of.", "Only if the client asks for."],
    correct: 2,
    rationale: "Pre-trade disclosure is a fundamental requirement to ensure informed consent for high-risk products.",
    topic: "Account Documentation", difficulty: "easy",
  },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TOPICS = [
  "Account Documentation",
  "Regulatory Filings & Reporting",
  "Registration & Licensing",
  "Margin Documentation",
  "CDCC & Clearing",
  "Market Integrity & UMIR",
  "Compliance & Supervision",
  "AML & FINTRAC",
];

const TOPIC_COLORS = {
  "Account Documentation":         { bg: "#dbeafe", text: "#1e40af", bar: "#3b82f6" },
  "Regulatory Filings & Reporting":{ bg: "#ccfbf1", text: "#0e7490", bar: "#06b6d4" },
  "Registration & Licensing":      { bg: "#ede9fe", text: "#5b21b6", bar: "#8b5cf6" },
  "Margin Documentation":          { bg: "#fef3c7", text: "#92400e", bar: "#f59e0b" },
  "CDCC & Clearing":               { bg: "#dcfce7", text: "#166534", bar: "#22c55e" },
  "Market Integrity & UMIR":       { bg: "#fee2e2", text: "#991b1b", bar: "#ef4444" },
  "Compliance & Supervision":      { bg: "#fce7f3", text: "#831843", bar: "#ec4899" },
  "AML & FINTRAC":                 { bg: "#f0fdf4", text: "#14532d", bar: "#16a34a" },
};

const DIFF_COLORS = {
  easy:   { bg: "#dcfce7", text: "#166534", dot: "#22c55e" },
  medium: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  hard:   { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
};

const OPTION_LABELS = ["A", "B", "C", "D"];

const QUIZ_PRESETS = [
  { label: "Quick Test",  count: 25,  mins: 40  },
  { label: "Half Exam",   count: 50,  mins: 80  },
  { label: "Full Exam",   count: 100, mins: 160 },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function fmtTime(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}
function calcScore(answers, qList) {
  return qList.filter((q, i) => answers[i] === q.correct).length;
}
function topicBreakdown(answers, qList) {
  const stats = {};
  TOPICS.forEach(t => { stats[t] = { total: 0, correct: 0 }; });
  qList.forEach((q, i) => {
    if (!stats[q.topic]) stats[q.topic] = { total: 0, correct: 0 };
    stats[q.topic].total++;
    if (answers[i] === q.correct) stats[q.topic].correct++;
  });
  return stats;
}
function diffBreakdown(answers, qList) {
  const st = { easy:{t:0,c:0}, medium:{t:0,c:0}, hard:{t:0,c:0} };
  qList.forEach((q,i) => {
    st[q.difficulty].t++;
    if (answers[i] === q.correct) st[q.difficulty].c++;
  });
  return st;
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const NAV = "#0f2744";   // deep navy
const GOLD = "#c9972b";
const NAVY2 = "#1e3f6b";

const S = {
  root: { minHeight:"100vh", background:"#f2f5f9", fontFamily:"'Georgia','Times New Roman',serif", color:"#0d1b2a" },
  card: { background:"#fff", borderRadius:16, border:"1px solid #dde4ee", boxShadow:"0 2px 14px rgba(0,0,0,0.06)", padding:28 },
  maxW: { maxWidth:900, margin:"0 auto", padding:"0 20px" },
  btnPrimary: { background:`linear-gradient(135deg,${NAV} 0%,${NAVY2} 100%)`, color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", cursor:"pointer", fontSize:14, fontWeight:600, letterSpacing:"0.03em", fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:8 },
  btnGold:    { background:`linear-gradient(135deg,${GOLD} 0%,#e0b846 100%)`, color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", cursor:"pointer", fontSize:14, fontWeight:700, letterSpacing:"0.05em", fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:8 },
  btnGhost:   { background:"transparent", color:"#52637a", border:"1px solid #dde4ee", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontSize:13, fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:6 },
  badge: (bg, text) => ({ display:"inline-flex", alignItems:"center", gap:4, background:bg, color:text, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:600, letterSpacing:"0.05em", fontFamily:"Georgia,serif" }),
};

function useFonts() {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Source+Serif+4:ital,wght@0,300;0,400;0,600;1,400&family=JetBrains+Mono:wght@400;600&display=swap";
    document.head.appendChild(l);
  }, []);
}

// ─── BRAND HEADER ─────────────────────────────────────────────────────────────
function BrandHeader() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ width:36, height:36, borderRadius:8, background:`linear-gradient(135deg,${NAV} 0%,${NAVY2} 100%)`, display:"flex", alignItems:"center", justifyContent:"center", color:GOLD, fontWeight:700, fontSize:14, letterSpacing:1, flexShrink:0 }}>
        V
      </div>
      <div>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:NAV, fontFamily:"Georgia,serif" }}>Vitta Finance Academy</div>
        <div style={{ fontSize:10, color:"#64748b", letterSpacing:"0.05em", fontFamily:"Georgia,serif" }}>CIRO Derivatives Exam Prep</div>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function ProgressBar({ value, max, color = NAV, thin = false }) {
  return (
    <div style={{ width:"100%", background:"#e4eaf2", borderRadius:999, height:thin?4:7, overflow:"hidden" }}>
      <div style={{ width:`${Math.min(100,(value/max)*100)}%`, height:"100%", background:color, borderRadius:999, transition:"width 0.3s ease" }} />
    </div>
  );
}

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────
function HomeScreen({ onStart }) {
  return (
    <div style={{ minHeight:"100vh", background:"#f2f5f9" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #dde4ee", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <BrandHeader />
          <div style={{ ...S.badge(NAV,"#fff"), fontSize:12, padding:"6px 14px" }}>Derivatives Exam · Element 2</div>
        </div>
      </div>

      <div style={{ ...S.maxW, padding:"56px 20px" }}>
        {/* Hero */}
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <div style={{ display:"inline-block", background:"linear-gradient(135deg,#f2f5f9,#e4eaf2)", border:"1px solid #c8d4e0", borderRadius:40, padding:"6px 20px", fontSize:12, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#52637a", marginBottom:20, fontFamily:"Georgia,serif" }}>
            Element 2 of 8
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:"clamp(30px,5vw,50px)", fontWeight:700, lineHeight:1.15, color:"#0d1b2a", margin:"0 0 16px", letterSpacing:"-0.01em" }}>
            Regulatory Requirements<br />&amp; Documentation
          </h1>
          <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:17, color:"#52637a", maxWidth:540, margin:"0 auto 28px", lineHeight:1.7 }}>
            CIRO registration, account agreements, CDCC clearing rules, UMIR market integrity, LOPR reporting, and AML documentation.
          </p>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:24, flexWrap:"wrap" }}>
            {[["100","Questions"],["8","Topics"],["3","Difficulty Levels"]].map(([n,l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:28, fontWeight:700, color:GOLD, lineHeight:1 }}>{n}</div>
                <div style={{ fontSize:11, color:"#52637a", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"Georgia,serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mode cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:24, marginBottom:36 }}>
          {/* Practice */}
          <div onClick={() => onStart("practice")} style={{ ...S.card, cursor:"pointer", border:"2px solid transparent", transition:"all 0.25s", position:"relative", overflow:"hidden" }}
            onMouseEnter={e => { e.currentTarget.style.border=`2px solid ${NAV}`; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=`0 10px 32px rgba(15,39,68,0.15)`; }}
            onMouseLeave={e => { e.currentTarget.style.border="2px solid transparent"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 14px rgba(0,0,0,0.06)"; }}>
            <div style={{ position:"absolute", top:0, right:0, width:80, height:80, background:"linear-gradient(135deg,#f2f5f9,#e4eaf2)", borderRadius:"0 16px 0 80px" }} />
            <div style={{ fontSize:32, marginBottom:16 }}>📋</div>
            <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700, margin:"0 0 10px", color:"#0d1b2a" }}>Practice Mode</h2>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, color:"#52637a", lineHeight:1.7, margin:"0 0 20px" }}>
              Work through all 100 questions at your own pace with instant feedback and regulatory rationales.
            </p>
            {["Immediate answer feedback","Full rationale explanations","Flag for review","Topic navigator"].map(f => (
              <div key={f} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"#52637a", fontFamily:"Georgia,serif", marginBottom:6 }}>
                <span style={{ color:"#22c55e", fontSize:14 }}>✓</span> {f}
              </div>
            ))}
            <div style={{ marginTop:20 }}>
              <button style={{ ...S.btnPrimary, width:"100%", justifyContent:"center" }}>Start Practice →</button>
            </div>
          </div>

          {/* Timed Quiz */}
          <div onClick={() => onStart("quiz-config")} style={{ ...S.card, cursor:"pointer", border:"2px solid transparent", transition:"all 0.25s", position:"relative", overflow:"hidden", background:"linear-gradient(160deg,#fff 70%,#fefbf0 100%)" }}
            onMouseEnter={e => { e.currentTarget.style.border=`2px solid ${GOLD}`; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 10px 32px rgba(201,151,43,0.2)"; }}
            onMouseLeave={e => { e.currentTarget.style.border="2px solid transparent"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 14px rgba(0,0,0,0.06)"; }}>
            <div style={{ position:"absolute", top:0, right:0, width:80, height:80, background:"linear-gradient(135deg,#fef3c7,#fde68a)", borderRadius:"0 16px 0 80px" }} />
            <div style={{ fontSize:32, marginBottom:16 }}>⏱</div>
            <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700, margin:"0 0 10px", color:"#0d1b2a" }}>Timed Quiz Mode</h2>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, color:"#52637a", lineHeight:1.7, margin:"0 0 20px" }}>
              Simulate real exam conditions with a countdown timer and full diagnostic breakdown on completion.
            </p>
            {["Randomized question order","Countdown timer","No hints until submission","Full diagnostic dashboard"].map(f => (
              <div key={f} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"#52637a", fontFamily:"Georgia,serif", marginBottom:6 }}>
                <span style={{ color:GOLD, fontSize:14 }}>✦</span> {f}
              </div>
            ))}
            <div style={{ marginTop:20 }}>
              <button style={{ ...S.btnGold, width:"100%", justifyContent:"center" }}>Configure Quiz →</button>
            </div>
          </div>
        </div>

        {/* Topic chips */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, fontWeight:600, color:"#0d1b2a", margin:"0 0 14px" }}>Topics Covered</h3>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {TOPICS.map(t => {
              const c = TOPIC_COLORS[t] || { bg:"#f2f5f9", text:"#52637a" };
              const count = QUESTIONS.filter(q => q.topic === t).length;
              return (
                <div key={t} style={{ display:"flex", alignItems:"center", gap:6, background:c.bg, color:c.text, borderRadius:20, padding:"5px 12px", fontSize:12, fontFamily:"Georgia,serif", fontWeight:600 }}>
                  {t} <span style={{ opacity:0.7, fontWeight:400 }}>({count})</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:44, color:"#94a3b8", fontSize:12, fontFamily:"Georgia,serif" }}>
          Authored by Amina Beecroft · Vitta Finance Academy · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── QUIZ CONFIG ──────────────────────────────────────────────────────────────
function QuizConfigScreen({ onBack, onStart }) {
  const [preset, setPreset] = useState(1);
  const p = QUIZ_PRESETS[preset];
  return (
    <div style={{ minHeight:"100vh", background:"#f2f5f9" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #dde4ee", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <BrandHeader />
          <button onClick={onBack} style={S.btnGhost}>← Back</button>
        </div>
      </div>
      <div style={{ ...S.maxW, padding:"56px 20px", maxWidth:600 }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <h1 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:34, fontWeight:700, margin:"0 0 10px", color:"#0d1b2a" }}>Configure Your Quiz</h1>
          <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#52637a", fontSize:16 }}>Questions drawn randomly from the full 100-question bank.</p>
        </div>
        <div style={{ ...S.card, marginBottom:22 }}>
          <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 16px", color:"#0d1b2a" }}>Select Quiz Length</h3>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            {QUIZ_PRESETS.map((pr,i) => (
              <div key={i} onClick={() => setPreset(i)} style={{ flex:1, minWidth:100, border:preset===i?`2px solid ${NAV}`:"1.5px solid #dde4ee", borderRadius:12, padding:"16px 12px", textAlign:"center", cursor:"pointer", background:preset===i?"#edf2f9":"#fff", transition:"all 0.2s" }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:26, fontWeight:700, color:preset===i?NAV:"#52637a" }}>{pr.count}</div>
                <div style={{ fontSize:11, color:"#52637a", fontFamily:"Georgia,serif", marginBottom:4 }}>questions</div>
                <div style={{ ...S.badge(preset===i?NAV:"#f2f5f9", preset===i?"#fff":"#52637a"), fontSize:10, padding:"2px 8px" }}>{pr.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...S.card, marginBottom:28 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            {[["⏱","Time Limit",`${p.mins} minutes`],["⚡","Avg per Q",`~${Math.round(p.mins*60/p.count)}s`],["📊","Coverage",p.count===100?"Full bank":`${p.count} random Q`],["🎯","Pass Mark","≥ 60% recommended"]].map(([icon,label,val]) => (
              <div key={label} style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <span style={{ fontSize:22 }}>{icon}</span>
                <div>
                  <div style={{ fontSize:11, color:"#94a3b8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
                  <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:16, fontWeight:600, color:"#0d1b2a" }}>{val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => onStart(p)} style={{ ...S.btnGold, width:"100%", justifyContent:"center", padding:"16px", fontSize:15 }}>
          Begin Timed Quiz →
        </button>
      </div>
    </div>
  );
}

// ─── QUESTION CARD ────────────────────────────────────────────────────────────
function QuestionCard({ q, qNum, total, selectedIndex, revealed, onSelect, onReveal, onFlag, isFlagged, mode }) {
  const tc = TOPIC_COLORS[q.topic] || { bg:"#f2f5f9", text:"#52637a" };
  const dc = DIFF_COLORS[q.difficulty];
  return (
    <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
      {/* Header */}
      <div style={{ padding:"18px 28px 14px", borderBottom:"1px solid #f0f4fa", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:12, color:"#94a3b8", fontWeight:600 }}>
            {String(qNum).padStart(2,"0")} / {String(total).padStart(2,"0")}
          </span>
          <div style={S.badge(tc.bg,tc.text)}>{q.topic}</div>
          <div style={S.badge(dc.bg,dc.text)}><span style={{ color:dc.dot, fontSize:8 }}>●</span>{q.difficulty.charAt(0).toUpperCase()+q.difficulty.slice(1)}</div>
        </div>
        <button onClick={() => onFlag(q.id)} style={{ ...S.btnGhost, border:isFlagged?"1.5px solid #f59e0b":"1px solid #dde4ee", color:isFlagged?"#d97706":"#94a3b8", padding:"5px 12px", fontSize:12, background:isFlagged?"#fef9ee":"transparent" }}>
          {isFlagged ? "🚩 Flagged" : "⚑ Flag"}
        </button>
      </div>
      {/* Question */}
      <div style={{ padding:"24px 28px 20px" }}>
        <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:16, lineHeight:1.75, color:"#0d1b2a", margin:0 }}>{q.text}</p>
      </div>
      {/* Options */}
      <div style={{ padding:"0 28px 24px", display:"flex", flexDirection:"column", gap:10 }}>
        {q.options.map((opt,i) => {
          let bg="#fff", border="1.5px solid #dde4ee", textColor="#0d1b2a", lBg="#f2f5f9", lColor="#52637a";
          if (revealed && mode==="practice") {
            if (i===q.correct)        { bg="#f0fdf4"; border="1.5px solid #86efac"; textColor="#166534"; lBg="#22c55e"; lColor="#fff"; }
            else if (i===selectedIndex){ bg="#fef2f2"; border="1.5px solid #fca5a5"; textColor="#991b1b"; lBg="#ef4444"; lColor="#fff"; }
          } else if (i===selectedIndex) {
            bg="#eff6ff"; border="1.5px solid #93c5fd"; textColor="#1e40af"; lBg="#3b82f6"; lColor="#fff";
          }
          return (
            <div key={i} onClick={() => !revealed && onSelect(i)}
              style={{ display:"flex", alignItems:"flex-start", gap:12, background:bg, border, borderRadius:10, padding:"13px 16px", cursor:revealed?"default":"pointer", transition:"all 0.15s" }}
              onMouseEnter={e => { if(!revealed && i!==selectedIndex){ e.currentTarget.style.border="1.5px solid #93c5fd"; e.currentTarget.style.background="#f8fbff"; } }}
              onMouseLeave={e => { if(!revealed && i!==selectedIndex){ e.currentTarget.style.border="1.5px solid #dde4ee"; e.currentTarget.style.background="#fff"; } }}>
              <span style={{ flexShrink:0, width:26, height:26, borderRadius:6, background:lBg, color:lColor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", transition:"all 0.15s" }}>
                {OPTION_LABELS[i]}
              </span>
              <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14.5, lineHeight:1.6, color:textColor, paddingTop:3 }}>
                {opt}
                {revealed && mode==="practice" && i===q.correct && <span style={{ marginLeft:8, fontSize:13, color:"#22c55e" }}>✓ Correct</span>}
                {revealed && mode==="practice" && i===selectedIndex && i!==q.correct && <span style={{ marginLeft:8, fontSize:13, color:"#ef4444" }}>✗ Incorrect</span>}
              </span>
            </div>
          );
        })}
      </div>
      {/* Rationale (practice only) */}
      {mode==="practice" && (
        <div style={{ padding:"0 28px 24px" }}>
          {!revealed ? (
            <button onClick={onReveal} disabled={selectedIndex===undefined||selectedIndex===null}
              style={{ ...S.btnPrimary, opacity:(selectedIndex!==undefined&&selectedIndex!==null)?1:0.4, cursor:(selectedIndex!==undefined&&selectedIndex!==null)?"pointer":"not-allowed", padding:"10px 24px", fontSize:13 }}>
              Check Answer
            </button>
          ) : (
            <div style={{ background:"#fefce8", border:"1px solid #fde68a", borderRadius:10, padding:"14px 18px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8, fontFamily:"'Playfair Display',Georgia,serif", fontSize:13, fontWeight:700, color:"#92400e" }}>💡 Rationale</div>
              <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, lineHeight:1.7, color:"#451a03", margin:0 }}>{q.rationale}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PRACTICE MODE ────────────────────────────────────────────────────────────
function PracticeMode({ onExit, onFinish }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [showNav, setShowNav] = useState(false);

  const qs = useMemo(() => {
    if (filter==="flagged") return QUESTIONS.filter(q => flagged.has(q.id));
    if (filter==="incorrect") return QUESTIONS.filter((q,i) => revealed[i] && answers[i]!==q.correct);
    return QUESTIONS;
  }, [filter, flagged, revealed, answers]);

  const q = qs[current] || QUESTIONS[0];
  const globalIdx = QUESTIONS.indexOf(q);
  const answered = Object.keys(answers).length;
  const correct = QUESTIONS.filter((q,i) => answers[i]===q.correct).length;

  return (
    <div style={{ minHeight:"100vh", background:"#f2f5f9" }}>
      {/* Sticky header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:"#fff", borderBottom:"1px solid #dde4ee", padding:"12px 20px", boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <BrandHeader />
          <div style={{ flex:1, minWidth:140 }}>
            <div style={{ fontSize:10, color:"#94a3b8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>
              Progress · {answered}/{QUESTIONS.length} answered · {correct} correct
            </div>
            <ProgressBar value={answered} max={QUESTIONS.length} />
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button onClick={() => setShowNav(!showNav)} style={{ ...S.btnGhost, fontSize:12 }}>☰ Navigator</button>
            {answered===QUESTIONS.length && (
              <button onClick={() => onFinish({ answers, mode:"practice" })} style={{ ...S.btnPrimary, padding:"8px 16px", fontSize:12 }}>View Results</button>
            )}
            <button onClick={onExit} style={{ ...S.btnGhost, fontSize:12, color:"#ef4444", borderColor:"#fca5a5" }}>✕ Exit</button>
          </div>
        </div>
        {showNav && (
          <div style={{ maxWidth:900, margin:"12px auto 0", background:"#f8fafc", borderRadius:12, border:"1px solid #dde4ee", padding:16 }}>
            <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
              {["all","flagged","incorrect"].map(f => (
                <button key={f} onClick={() => { setFilter(f); setCurrent(0); }} style={{ ...S.btnGhost, background:filter===f?NAV:"transparent", color:filter===f?"#fff":"#52637a", fontSize:11, padding:"5px 12px", borderColor:filter===f?NAV:"#dde4ee" }}>
                  {f==="all"?`All (${QUESTIONS.length})`:f==="flagged"?`Flagged (${flagged.size})`:`Incorrect (${QUESTIONS.filter((q,i)=>revealed[i]&&answers[i]!==q.correct).length})`}
                </button>
              ))}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {qs.map((q,i) => {
                const gi=QUESTIONS.indexOf(q), isAns=answers[gi]!==undefined, isRev=revealed[gi], isCorr=isAns&&answers[gi]===q.correct, isFlag=flagged.has(q.id);
                let bg="#f2f5f9", color="#52637a", border="1px solid #dde4ee";
                if(isFlag){bg="#fef9ee";color="#d97706";border="1px solid #fde68a";}
                if(isRev){bg=isCorr?"#dcfce7":"#fee2e2";color=isCorr?"#166534":"#991b1b";border=`1px solid ${isCorr?"#86efac":"#fca5a5"}`;}
                if(i===current){border=`2px solid ${NAV}`;}
                return (
                  <button key={q.id} onClick={() => { setCurrent(i); setShowNav(false); }} style={{ width:34, height:34, borderRadius:7, background:bg, color, border, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:"'JetBrains Mono',monospace", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                    {q.id}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...S.maxW, padding:"28px 20px" }}>
        {qs.length===0 ? (
          <div style={{ ...S.card, textAlign:"center", padding:48 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20 }}>No questions in this filter</h3>
            <button onClick={() => setFilter("all")} style={S.btnPrimary}>View All Questions</button>
          </div>
        ) : (
          <>
            <QuestionCard q={q} qNum={current+1} total={qs.length} selectedIndex={answers[globalIdx]} revealed={!!revealed[globalIdx]}
              onSelect={i => setAnswers(a => ({...a,[globalIdx]:i}))}
              onReveal={() => setRevealed(r => ({...r,[globalIdx]:true}))}
              onFlag={id => setFlagged(f => { const n=new Set(f); n.has(id)?n.delete(id):n.add(id); return n; })}
              isFlagged={flagged.has(q.id)} mode="practice" />
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:20, flexWrap:"wrap", gap:12 }}>
              <button onClick={() => setCurrent(c => Math.max(0,c-1))} disabled={current===0} style={{ ...S.btnGhost, opacity:current===0?0.4:1 }}>← Previous</button>
              <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, color:"#94a3b8" }}>{current+1} of {qs.length}</span>
              {current<qs.length-1
                ? <button onClick={() => setCurrent(c => c+1)} style={S.btnPrimary}>Next →</button>
                : <button onClick={() => onFinish({answers,mode:"practice"})} style={S.btnGold}>View Results →</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── QUIZ MODE ────────────────────────────────────────────────────────────────
function QuizMode({ config, onExit, onFinish }) {
  const quizQs = useMemo(() => shuffle([...Array(100)].map((_,i)=>i)).slice(0,config.count).map(i=>QUESTIONS[i]), [config.count]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(config.mins*60);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const timerRef = useRef(null);
  const timeLeftRef = useRef(config.mins*60);

  const handleSubmit = useCallback(() => {
    clearInterval(timerRef.current);
    const timeUsed = config.mins*60 - timeLeftRef.current;
    onFinish({ answers, quizQs, mode:"quiz", timeUsed, timeLimit:config.mins*60 });
  }, [answers, quizQs, config, onFinish]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        const next = t-1;
        timeLeftRef.current = next;
        if (next<=0) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [handleSubmit]);

  const answered = Object.keys(answers).length;
  const isLowTime = timeLeft<300;
  const timerColor = isLowTime?"#dc2626":timeLeft<600?"#d97706":"#0d1b2a";
  const q = quizQs[current];

  return (
    <div style={{ minHeight:"100vh", background:"#f2f5f9" }}>
      <div style={{ position:"sticky", top:0, zIndex:100, background:"#fff", borderBottom:"1px solid #dde4ee", padding:"12px 20px", boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <BrandHeader />
          <div style={{ display:"flex", alignItems:"center", gap:8, background:isLowTime?"#fef2f2":"#edf2f9", border:`1.5px solid ${isLowTime?"#fca5a5":"#c8d4e0"}`, borderRadius:8, padding:"6px 14px", transition:"all 0.5s" }}>
            <span style={{ fontSize:14 }}>{isLowTime?"🔴":"⏱"}</span>
            <span style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:20, fontWeight:700, color:timerColor, letterSpacing:"0.05em" }}>{fmtTime(timeLeft)}</span>
          </div>
          <div style={{ flex:1, minWidth:120 }}>
            <div style={{ fontSize:10, color:"#94a3b8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{answered}/{quizQs.length} answered</div>
            <ProgressBar value={answered} max={quizQs.length} />
          </div>
          <button onClick={() => setConfirmSubmit(true)} style={{ ...S.btnPrimary, padding:"8px 16px", fontSize:12 }}>Submit Quiz</button>
          <button onClick={onExit} style={{ ...S.btnGhost, fontSize:12, color:"#ef4444" }}>✕</button>
        </div>
        <div style={{ maxWidth:900, margin:"4px auto 0" }}>
          <ProgressBar value={current+1} max={quizQs.length} color={GOLD} thin />
        </div>
      </div>

      {confirmSubmit && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, padding:20 }}>
          <div style={{ ...S.card, maxWidth:400, width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20, margin:"0 0 8px" }}>Submit Quiz?</h3>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#52637a", fontSize:14, margin:"0 0 20px" }}>
              You've answered <strong>{answered}</strong> of <strong>{quizQs.length}</strong> questions.
              {answered<quizQs.length && ` ${quizQs.length-answered} unanswered will be marked incorrect.`}
            </p>
            <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
              <button onClick={() => setConfirmSubmit(false)} style={S.btnGhost}>Keep Going</button>
              <button onClick={handleSubmit} style={S.btnGold}>Submit & See Results</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...S.maxW, padding:"28px 20px" }}>
        <QuestionCard q={q} qNum={current+1} total={quizQs.length} selectedIndex={answers[current]} revealed={false}
          onSelect={i => setAnswers(a => ({...a,[current]:i}))} onReveal={() => {}} onFlag={() => {}} isFlagged={false} mode="quiz" />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:20, flexWrap:"wrap", gap:12 }}>
          <button onClick={() => setCurrent(c => Math.max(0,c-1))} disabled={current===0} style={{ ...S.btnGhost, opacity:current===0?0.4:1 }}>← Previous</button>
          <div style={{ display:"flex", gap:4, flexWrap:"wrap", maxWidth:400, justifyContent:"center" }}>
            {quizQs.map((_,i) => (
              <div key={i} onClick={() => setCurrent(i)} style={{ width:i===current?16:8, height:8, borderRadius:4, cursor:"pointer", background:i===current?NAV:answers[i]!==undefined?GOLD:"#c8d4e0", transition:"all 0.2s" }} />
            ))}
          </div>
          {current<quizQs.length-1
            ? <button onClick={() => setCurrent(c => c+1)} style={S.btnPrimary}>Next →</button>
            : <button onClick={() => setConfirmSubmit(true)} style={S.btnGold}>Submit →</button>}
        </div>
      </div>
    </div>
  );
}

// ─── RESULTS SCREEN ───────────────────────────────────────────────────────────
function ResultsScreen({ data, onRetry, onHome }) {
  const { answers, mode, timeUsed } = data;
  const questionList = data.quizQs || QUESTIONS;
  const [reviewFilter, setReviewFilter] = useState("all");
  const [expandedQ, setExpandedQ] = useState(null);

  const total = questionList.length;
  const correct = calcScore(answers, questionList);
  const pct = Math.round((correct/total)*100);
  const passed = pct>=60;
  const topicStats = useMemo(() => topicBreakdown(answers,questionList),[answers,questionList]);
  const diffStats = useMemo(() => diffBreakdown(answers,questionList),[answers,questionList]);

  const reviewQs = useMemo(() => {
    return questionList.map((q,i) => ({ q, i, selected:answers[i], isCorrect:answers[i]===q.correct }))
      .filter(item => reviewFilter==="incorrect"?!item.isCorrect:reviewFilter==="correct"?item.isCorrect:true);
  }, [questionList,answers,reviewFilter]);

  const scoreColor = pct>=75?"#166534":pct>=60?"#92400e":"#991b1b";
  const scoreBg    = pct>=75?"#dcfce7":pct>=60?"#fef3c7":"#fee2e2";

  return (
    <div style={{ minHeight:"100vh", background:"#f2f5f9" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #dde4ee", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <BrandHeader />
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onRetry} style={S.btnGhost}>↺ Try Again</button>
            <button onClick={onHome} style={S.btnPrimary}>⌂ Home</button>
          </div>
        </div>
      </div>

      <div style={{ ...S.maxW, padding:"32px 20px" }}>
        {/* Score hero */}
        <div style={{ ...S.card, marginBottom:24, textAlign:"center", padding:"40px 28px" }}>
          <div style={{ display:"inline-block", background:scoreBg, color:scoreColor, borderRadius:20, padding:"4px 16px", fontSize:12, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"Georgia,serif", marginBottom:16 }}>
            {passed?"✓ Pass":"✗ Needs Review"} · Element 2: Regulatory Requirements
          </div>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:"clamp(56px,10vw,80px)", fontWeight:700, color:scoreColor, lineHeight:1, marginBottom:8 }}>{pct}%</div>
          <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#52637a", fontSize:16, marginBottom:24 }}>{correct} correct out of {total} questions</div>
          <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap" }}>
            {[
              ["✓",String(correct),"Correct","#166534","#dcfce7"],
              ["✗",String(total-correct),"Incorrect","#991b1b","#fee2e2"],
              ...(mode==="quiz"&&timeUsed ? [["⏱",fmtTime(timeUsed),"Time Used","#1e40af","#dbeafe"],["⚡",`${Math.round(timeUsed/total)}s`,"Avg / Q","#92400e","#fef3c7"]] : []),
            ].map(([icon,val,label,tc,bg]) => (
              <div key={label} style={{ background:bg, color:tc, borderRadius:12, padding:"14px 20px", minWidth:90, textAlign:"center" }}>
                <div style={{ fontSize:20 }}>{icon}</div>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700 }}>{val}</div>
                <div style={{ fontSize:11, fontFamily:"Georgia,serif", opacity:0.8, letterSpacing:"0.05em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Breakdown */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20, marginBottom:24 }}>
          {/* Topics */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 20px", color:"#0d1b2a" }}>Performance by Topic</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {TOPICS.filter(t => topicStats[t]&&topicStats[t].total>0).map(t => {
                const st=topicStats[t], tp=Math.round((st.correct/st.total)*100), c=TOPIC_COLORS[t]||{bar:"#94a3b8"};
                return (
                  <div key={t}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:12, fontFamily:"Georgia,serif", color:"#0d1b2a", fontWeight:600 }}>{t}</span>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:tp>=60?"#166534":"#991b1b", fontWeight:700 }}>{st.correct}/{st.total} ({tp}%)</span>
                    </div>
                    <div style={{ background:"#e4eaf2", borderRadius:999, height:8, overflow:"hidden" }}>
                      <div style={{ width:`${tp}%`, height:"100%", background:c.bar, borderRadius:999, transition:"width 0.8s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Difficulty */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 20px", color:"#0d1b2a" }}>Performance by Difficulty</h3>
            {[["easy","Easy"],["medium","Medium"],["hard","Hard"]].map(([key,label]) => {
              const st=diffStats[key], tp=st.t>0?Math.round((st.c/st.t)*100):0, dc=DIFF_COLORS[key];
              return (
                <div key={key} style={{ marginBottom:18 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ color:dc.dot, fontSize:12 }}>●</span>
                      <span style={{ fontSize:13, fontFamily:"Georgia,serif", color:"#0d1b2a", fontWeight:600 }}>{label}</span>
                    </div>
                    <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:tp>=60?"#166534":"#991b1b", fontWeight:700 }}>{st.c}/{st.t} ({tp}%)</span>
                  </div>
                  <div style={{ background:"#e4eaf2", borderRadius:999, height:10, overflow:"hidden" }}>
                    <div style={{ width:`${tp}%`, height:"100%", background:dc.dot, borderRadius:999, transition:"width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
            {diffStats.hard.t>0&&diffStats.hard.c/diffStats.hard.t<0.6 && (
              <div style={{ background:"#fef9ee", border:"1px solid #fde68a", borderRadius:10, padding:"12px 14px", marginTop:8 }}>
                <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#92400e", margin:0, lineHeight:1.6 }}>
                  💡 Focus on <strong>Hard</strong> questions — you scored {Math.round(diffStats.hard.c/diffStats.hard.t*100)}% in that category.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Review */}
        <div style={{ ...S.card }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:0, color:"#0d1b2a" }}>Question Review</h3>
            <div style={{ display:"flex", gap:8 }}>
              {["all","incorrect","correct"].map(f => (
                <button key={f} onClick={() => setReviewFilter(f)} style={{ ...S.btnGhost, background:reviewFilter===f?NAV:"transparent", color:reviewFilter===f?"#fff":"#52637a", borderColor:reviewFilter===f?NAV:"#dde4ee", fontSize:11, padding:"5px 12px" }}>
                  {f==="all"?`All (${total})`:f==="incorrect"?`Incorrect (${total-correct})`:`Correct (${correct})`}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {reviewQs.map(({ q, i, selected, isCorrect }) => (
              <div key={q.id} style={{ border:`1px solid ${isCorrect?"#86efac":"#fca5a5"}`, borderRadius:10, background:isCorrect?"#f0fdf4":"#fef2f2", overflow:"hidden" }}>
                <div onClick={() => setExpandedQ(expandedQ===q.id?null:q.id)} style={{ padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
                  <span style={{ flexShrink:0, fontSize:16, marginTop:1 }}>{selected===undefined?"○":isCorrect?"✓":"✗"}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#94a3b8" }}>Q{q.id}</span>
                      <div style={S.badge(TOPIC_COLORS[q.topic]?.bg||"#f2f5f9",TOPIC_COLORS[q.topic]?.text||"#52637a")}>{q.topic}</div>
                      <div style={S.badge(DIFF_COLORS[q.difficulty].bg,DIFF_COLORS[q.difficulty].text)}>{q.difficulty}</div>
                    </div>
                    <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#0d1b2a", margin:0, lineHeight:1.5 }}>
                      {q.text.length>120?q.text.slice(0,120)+"…":q.text}
                    </p>
                  </div>
                  <span style={{ flexShrink:0, color:"#94a3b8", fontSize:12 }}>{expandedQ===q.id?"▲":"▼"}</span>
                </div>
                {expandedQ===q.id && (
                  <div style={{ padding:"0 16px 16px 44px" }}>
                    <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13.5, color:"#0d1b2a", margin:"0 0 10px", lineHeight:1.7 }}>{q.text}</p>
                    {q.options.map((opt,oi) => {
                      let bg="#fff", border="1px solid #dde4ee", color="#0d1b2a";
                      if(oi===q.correct){bg="#f0fdf4";border="1px solid #86efac";color="#166534";}
                      else if(oi===selected){bg="#fef2f2";border="1px solid #fca5a5";color="#991b1b";}
                      return (
                        <div key={oi} style={{ display:"flex", gap:8, background:bg, border, borderRadius:8, padding:"8px 12px", marginBottom:6 }}>
                          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color, fontWeight:700, flexShrink:0 }}>{OPTION_LABELS[oi]}</span>
                          <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color, lineHeight:1.5 }}>
                            {opt}
                            {oi===q.correct&&<span style={{ marginLeft:6, fontSize:11 }}>✓ Correct Answer</span>}
                            {oi===selected&&oi!==q.correct&&<span style={{ marginLeft:6, fontSize:11 }}>✗ Your Answer</span>}
                          </span>
                        </div>
                      );
                    })}
                    <div style={{ background:"#fefce8", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginTop:8 }}>
                      <span style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:12, fontWeight:700, color:"#92400e" }}>💡 Rationale:</span>
                      <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#451a03", marginLeft:8, lineHeight:1.6 }}>{q.rationale}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:44, color:"#94a3b8", fontSize:12, fontFamily:"Georgia,serif" }}>
          Vitta Finance Academy · Element 2 of 8 · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  useFonts();
  const [screen, setScreen] = useState("home");
  const [quizConfig, setQuizConfig] = useState(null);
  const [results, setResults] = useState(null);

  return (
    <div style={S.root}>
      {screen==="home" && <HomeScreen onStart={m => { if(m==="practice")setScreen("practice"); else setScreen("quiz-config"); }} />}
      {screen==="practice" && <PracticeMode onExit={() => setScreen("home")} onFinish={d => { setResults(d); setScreen("results"); }} />}
      {screen==="quiz-config" && <QuizConfigScreen onBack={() => setScreen("home")} onStart={c => { setQuizConfig(c); setScreen("quiz"); }} />}
      {screen==="quiz" && quizConfig && <QuizMode config={quizConfig} onExit={() => setScreen("home")} onFinish={d => { setResults(d); setScreen("results"); }} />}
      {screen==="results" && results && <ResultsScreen data={results} onRetry={() => { setResults(null); setScreen(results?.mode==="quiz"?"quiz-config":"home"); }} onHome={() => { setResults(null); setScreen("home"); }} />}
    </div>
  );
}
